import { DataSourceExecutionEvent, LookupModelSearchEvent, LookupModelSelectionEvent, Layout, SlideoutDecorator, TableRow, TableSelectionEvent, MultiButton, ClickEvent, TableRowDisplayEvent, Label } from "@mcleod/components";
import { AutogenLayoutDataFusionInvoicesFromBillingHistory } from "./autogen/AutogenLayoutDataFusionInvoicesFromBillingHistory";
import { Currency, DateRange, ModelRow, StringUtil } from "@mcleod/core";
import { DataFusionInvoicesTransferSlideout } from "./DataFusionInvoicesTransferSlideout";
import { PlanningUtil } from "@mcleod/dispatch/src/planning/PlanningUtil";

const CHECK_AMOUNT_FIELD = "totalcharge_and_excisetax";
export class DataFusionInvoicesFromBillingHistory extends AutogenLayoutDataFusionInvoicesFromBillingHistory {
    private version = "";
    private partner_id = "";
    private alt_partner_id = "";
    private transaction_set = "";
    private _selectedCheckAmountSum: Currency;
    private _selectedRowCount: number;
    private _multiButtonPrintAndPost: MultiButton;
    private _selectedRows: TableRow[];
    private _dateRange: DateRange;
    private _search_partner: string;
    private _search_customer: string;
    private _search_billing_user: string;
    private _isTransferring: boolean = false;


    onLoad(): void | Promise<void> {
        this.multiButtonPrintAndPost = new MultiButton({
            color: "primary.reverse",
            backgroundColor: "primary",
            width: 80,
            enabled: false,
            showSeparator: true,
        });
        this.multiButtonPrintAndPost.primaryButton.setProps({
            themeKey: "editRowDecorator.primaryButton",
            id: "multiButtonPrintAndPost",
            caption: "Transfer",
            enabled: false,
            tooltip: "Transfer invoices",
            onClick: () => {
                this.buttonTransferOnClick()
            },
        });
        this.tableMissingBills.addRightTool(this.multiButtonPrintAndPost);
    }

    get selectedRows(): TableRow[] {
        return this._selectedRows;
    }

    set selectedRows(value: TableRow[]) {
        this._selectedRows = value;
    }

    get selectedRowCount(): number {
        return this._selectedRowCount;
    }

    set selectedRowCount(value: number) {
        this._selectedRowCount = value;
    }

    get multiButtonPrintAndPost(): MultiButton {
        return this._multiButtonPrintAndPost;
    }

    set multiButtonPrintAndPost(value: MultiButton) {
        this._multiButtonPrintAndPost = value;
    }

    get selectedCheckAmountSum(): Currency {
        return this._selectedCheckAmountSum;
    }

    set selectedCheckAmountSum(value: Currency) {
        this._selectedCheckAmountSum = value;
    }

    get dateRange(): DateRange {
        return this._dateRange;
    }

    set dateRange(value: DateRange) {
        this._dateRange = value;
    }

    get search_partner(): string {
        return this._search_partner;
    }

    set search_partner(value: string) {
        this._search_partner = value;
    }

    get search_customer(): string {
        return this._search_customer;
    }

    set search_customer(value: string) {
        this._search_customer = value;
    }

    get search_billing_user(): string {
        return this._search_billing_user;
    }
    set search_billing_user(value: string) {
        this._search_billing_user = value;
    }

    get isTransferring(): boolean {
        return this._isTransferring;
    }

    set isTransferring(value: boolean) {
        this._isTransferring = value;
    }

    //save most recent search parameters for refresh and report dateRange
    searchpanelBillingHistoryOnDataDisplay() {
        if(!this.isTransferring) {
            this.dateRange = this.textboxRangeOfDates.getDateRange();
            this.search_partner = this.textboxPartnerId.getDataValue();
            this.search_customer = this.textboxCustomer.getDataValue();
            this.search_billing_user = this.textboxBillingUser.getDataValue();
        }
        this.multiButtonPrintAndPost.enabled = false;
        this.isTransferring = false;
    }

    //filter for search based off parameters
    sourceDataFusionInvoicesFromHistoryBeforeExecution(event: DataSourceExecutionEvent) {
        if (!StringUtil.isEmptyString(this.version)) {
            event.filter.version = this.version;
        }
        if (!StringUtil.isEmptyString(this.alt_partner_id)) {
            event.filter.alt_partner_id = this.alt_partner_id;
        }
        if (!StringUtil.isEmptyString(this.transaction_set)) {
            event.filter.transaction_set = this.transaction_set;
        }
    }

    //adds the invoice total
    sourceDataFusionInvoicesFromHistoryAfterExecution() {
        this.labelAmountOutstanding.visible = true;
        this.mainDataSource.activeRow?.set("invoice_total", this.mainDataSource.summaryData.get("invoice_total"));
    }

    //uses info from partner selection (if available) to display only related entries
    textboxCustomerBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.transaction_type = "I";
        event.filter.direction = "O";
        if (!StringUtil.isEmptyString(this.version)) {
            event.filter.version = this.version;
        }
        if (!StringUtil.isEmptyString(this.partner_id)) {
            event.filter.partner_id = this.partner_id;
        }
        if (!StringUtil.isEmptyString(this.alt_partner_id)) {
            event.filter.alt_partner_id = this.alt_partner_id;
        }
    }

    //applies direction filter to partner lookup
    textboxPartnerIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.direction = "O";
    }

    //gets info from partner selection in order to filter the results of customer lookup model to related entries only
    textboxPartnerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
         if (event.hasSelection() === true) {
            const selectedRow = event.selection;
            this.version = selectedRow.get("version");
            this.partner_id = selectedRow.get("partner_id");
            this.alt_partner_id = selectedRow.get("alt_partner_id");
            this.transaction_set = selectedRow.get("transaction_set");

            //prevents unrelated partners and customers from being selected
            this.textboxCustomer.clear();
         }
         else {
            this.partner_id = "";
            this.alt_partner_id = "";
         }
    }

    async onTableSelection(event: TableSelectionEvent) {
        const tableRow = event?.newRow;
        this.selectedRows = tableRow?.table?.selectedRows;
        
        if (this.selectedRows?.length > 0) {
            this.multiButtonPrintAndPost.enabled = true;
            this.selectedCheckAmountSum = await this.calculateSelectedCheckAmountSum(this.selectedRows);
            this.selectedRowCount = this.selectedRows?.length;
            this.multiButtonPrintAndPost.showSeparator = true;
        } else {
            this.multiButtonPrintAndPost.enabled = false;
            this.multiButtonPrintAndPost.showSeparator = true;
        }
    }

    /**
     * Calculates the sum of check amounts for the selected rows
     */
    async calculateSelectedCheckAmountSum(selectedRows: TableRow[]): Promise<Currency> {
        const sum: Currency = new Currency(0);

        for (const row of selectedRows) {
            const checkAmount = row.data.getCurrency(CHECK_AMOUNT_FIELD);
            if (checkAmount) {
                await sum.add(checkAmount);
            }
        }

        return sum;
    }

    //brings out the posting slideout to post selected invoices
    buttonTransferOnClick() {
        this.multiButtonPrintAndPost.busy = true;

        const layout = Layout.getLayout("lme/datafusion/DataFusionInvoicesTransferSlideout", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as DataFusionInvoicesTransferSlideout;

        layout.parentBillingHistory = this;
        const selectedInvoiceIds = this.getSelectedInvoiceIds();
        layout.selectedInvoiceIds = selectedInvoiceIds;

        //re-search to refresh table rows and total amount outstanding
        layout.postingCallback = () => this.mainDataSource.search({
            partner_id: this.search_partner,
            customer_id: this.search_customer,
            date_range_start: this.dateRange.startDate,
            date_range_end: this.dateRange.endDate,
            billing_user: this.search_billing_user
        });

        new SlideoutDecorator({
            layout: layout,
            fillVerticalSpace: true,
            title: `DataFusion Transferred From Invoice History`,
            doAfterSlideIn: async () => {
                layout.selectedCheckAmountSum = this.selectedCheckAmountSum;
                layout.selectedRowCount = this.selectedRowCount;
                layout.dateRange = this.dateRange;
                this.multiButtonPrintAndPost.busy = true;
                await layout.postInvoiceTransactions();
                this.multiButtonPrintAndPost.busy = false;
            }
        });
    }

    //maps the ids of the selected rows to a variable for processing
    public getSelectedInvoiceIds(): string[] {
        return this.selectedRows.map(row => row.data.get("id") as string);
    }

    
    labelOrderIdOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    tableMissingBillsOnRowDisplay(event: TableRowDisplayEvent) {
        const modelRow = event.getTableRow().data as ModelRow;
        const tableRow = event.getTableRow();
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        if (modelRow.get("order_id")) labelOrderId.addUnderlineWhenMouseOver();

    }

}
